/**
 * 模組頁面代碼 : 1420-UI-01
 * 頁面說明 : 客戶資料清單列表頁面
 * @module modules/customer/components/CustomerPage
 */

// ----- import from npm modules -----
import React from 'react';

// ----- import from base libraries -----

import {useEffect, useState, useRef} from 'react';
import {showMessage} from 'web/utils/modalHelper';
import {BaseComponent} from 'web/components/BaseComponent';
import enumList from 'web/utils/enumList';
import {useDispatch} from 'react-redux';
import {useCustomer} from '../hooks/useCustomer';

const CustomerPage = (props) => {
  const dispatch = useDispatch();
  const baseComponent = new BaseComponent(props);
  const {
    onBatchHandle,
    needCustomerLoad,
    toggleCustomerAddModal,
    toggleCustomerEditModal,
    setNeedCustomerLoad,
    setMachineAssignmentCustomerData,
    toggleMachineAssignmentListModal,
  } = props;
  const [searchCustomerName, setSearchCustomerName] = useState('');
  const [searchCustomerCode, setSearchCustomerCode] = useState('');
  const [searchContactName, setSearchContactName] = useState('');
  const tableRef = useRef(null);

  const {customerList, getCustomerList, onBatch} = useCustomer(dispatch, showMessage, onBatchHandle, baseComponent);

  useEffect(() => {
    baseComponent.checkPermission(enumList.moduleEnums.customer, enumList.accessModeEnums.read);
    getCustomerList(searchCustomerName, searchCustomerCode, searchContactName);
    setNeedCustomerLoad(false);
  }, [needCustomerLoad]);

  useEffect(() => {
    const handleClick = (event) => {
      const button = event.target.closest('.btn');
      if (button && button.id.startsWith('edit-customer-')) {
        toggleCustomerEditModal(true, customerList[parseInt(button.id.split('-')[2])]);
      }
      const checkbox = event.target.closest('input[type="checkbox"]');
      if (checkbox && checkbox.id.startsWith('eab-customer-')) {
        const index = parseInt(checkbox.id.split('-')[2]);
        const mode = checkbox.checked ? enumList.alertModalModeEnums.enable : enumList.alertModalModeEnums.disable;
        const updateCustomer = customerList[index];
        if (updateCustomer) {
          let message = '';
          if (mode === enumList.alertModalModeEnums.enable) {
            message = `啟用 [ ${updateCustomer.customerName} ] 的資料`;
          } else {
            message = `停用 [ ${updateCustomer.customerName} ] 的資料`;
          }
          onBatchHandle(mode, message, [updateCustomer.customerCode]);
        } else {
          console.log('No case found at index:', index);
        }
      }
    };
    const handleMahineBindingClick = (event) => {
      /** @type {HTMLButtonElement} */
      const target = event.target;
      if (!target.classList.contains('customer-machine-bindings')) {
        return;
      }
      const {row: rowNumber} = target.dataset;
      const customerData = customerList[rowNumber];
      setMachineAssignmentCustomerData(customerData);
      toggleMachineAssignmentListModal('show');
    };

    if (tableRef.current) {
      tableRef.current.addEventListener('click', handleClick);
      tableRef.current.addEventListener('click', handleMahineBindingClick);
    }

    return () => {
      if (tableRef.current) {
        tableRef.current.removeEventListener('click', handleClick);
        tableRef.current.removeEventListener('click', handleMahineBindingClick);
      }
    };
  }, [customerList]);

  const handleSearch = (e) => {
    e.preventDefault();
    const searchForm = new FormData(e.target);
    const customerName = searchForm.get('searchCustomerName');
    const customerCode = searchForm.get('searchCustomerCode');
    const contactName = searchForm.get('searchContactName');

    getCustomerList(customerName, customerCode, contactName);
  };

  const onNewDepartment = () => {
    //   this.props.onNewElevator();
  };

  return (
    <section id="datatable-container">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <svg className="nav__icon nav__icon--breadcrumb me-3" aria-hidden="true">
              <use href="#ico-home-line"></use>
            </svg>
            基本資料
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            客戶資料
          </li>
        </ol>
      </nav>
      <h4>客戶資料</h4>
      <div className="datatable__search">
        <form className="d-flex gap-2 align-items-end needs-validation" noValidate onSubmit={handleSearch}>
          <div className="flex-fill w-100">
            <label htmlFor="searchCustomerName" className="form-label">
              客戶名稱
            </label>
            <input
              className="form-control form-control-sm"
              id="searchCustomerName"
              name="searchCustomerName"
              value={searchCustomerName}
              onChange={(e) => setSearchCustomerName(e.target.value)}
              required
            />
          </div>
          <div className="flex-fill w-100">
            <label htmlFor="searchCustomerCode" className="form-label">
              客戶代碼
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              id="searchCustomerCode"
              name="searchCustomerCode"
              value={searchCustomerCode}
              onChange={(e) => setSearchCustomerCode(e.target.value)}
              required
            />
          </div>
          <div className="flex-fill w-100">
            <label htmlFor="searchContactName" className="form-label">
              聯絡人
            </label>
            <input
              className="form-control form-control-sm"
              id="searchContactName"
              name="searchContactName"
              value={searchContactName}
              onChange={(e) => setSearchContactName(e.target.value)}
              required
            />
          </div>
          <div>
            <button className="btn btn-primary" type="submit">
              <svg className="btn__icon" aria-hidden="true">
                <use href="#ico-search"></use>
              </svg>
            </button>
          </div>
        </form>
      </div>
      <div className="datatable__tool">
        <div className="d-flex align-items-center gap-2">
          <div className="datatable__tool__label">
            多筆選擇
            <div id="checked-count-display"></div>
          </div>
          {baseComponent.checkPermission(enumList.moduleEnums.customer, enumList.accessModeEnums.delete) && (
            <div className="btn-group btn-group-sm" role="group" aria-label="">
              <button
                type="button"
                className="btn btn-outline-secondary btn-batch"
                id="btnBatchEnable"
                onClick={() => onBatch(enumList.alertModalModeEnums.enable)}
              >
                啟用
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-batch"
                id="btnBatchDisable"
                onClick={() => onBatch(enumList.alertModalModeEnums.disable)}
              >
                停用
              </button>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {baseComponent.checkPermission(enumList.moduleEnums.customer, enumList.accessModeEnums.create) && (
            <button type="button" className="btn btn-sm btn-primary" id="modalElevatorBtnNew" onClick={toggleCustomerAddModal}>
              <svg className="btn__icon me-2" aria-hidden="true">
                <use href="#ico-plus-square"></use>
              </svg>
              新增
            </button>
          )}
        </div>
      </div>
      <div className="datatable__data" ref={tableRef}>
        <table id="datatable__customerList" className="display table table-hover align-middle nowrap"></table>
      </div>
    </section>
  );
};

export default CustomerPage;
